import React from 'react';
import PropTypes from 'prop-types';
import styles from './BulkActionBar.module.css';

// Strip rendered above a table at all times — even when nothing is selected.
// The select-all checkbox and the action buttons are always visible so the
// bulk operations are discoverable; the buttons are simply disabled until one
// or more rows are selected. (An earlier version collapsed the whole bar on an
// empty selection, which hid the only select-all checkbox too — users never
// found the bulk features and raised support calls.) Pages remove the
// select-all from their table header in favour of this one.

const variantClass = (variant) => {
  switch (variant) {
    case 'danger':
      return styles.btnDanger;
    case 'primary':
      return styles.btnPrimary;
    case 'info':
      return styles.btnInfo;
    case 'secondary':
    default:
      return styles.btnSecondary;
  }
};

const BulkActionBar = ({
  count,
  itemLabel = 'item',
  actions,
  onClear,
  allSelected,
  someSelected,
  onToggleAll,
}) => {
  const hasSelection = count > 0;
  const selectFirstHint = `Select ${itemLabel}s first`;

  return (
    <div className={`${styles.bar} ${hasSelection ? styles.barActive : ''}`}>
      <label className={styles.left}>
        <input
          type="checkbox"
          className={styles.selectAllCheckbox}
          checked={!!allSelected}
          ref={(el) => {
            if (el) el.indeterminate = !!someSelected && !allSelected;
          }}
          onChange={onToggleAll}
          aria-label="Select all"
        />
        <span className={styles.countLabel}>
          {hasSelection ? (
            <>
              <strong>{count}</strong>{' '}
              {count === 1 ? itemLabel : `${itemLabel}s`} selected
            </>
          ) : (
            'Select All'
          )}
        </span>
      </label>
      <div className={styles.actions}>
        {actions.map((action, i) => {
          // With nothing selected every button is disabled and its tooltip
          // tells the user what to do instead of describing the action.
          const tooltip = hasSelection
            ? action.tooltip || action.title
            : selectFirstHint;
          return (
            <div key={action.label || i} className={styles.actionItem}>
              <button
                className={`${styles.actionBtn} ${variantClass(action.variant)}`}
                onClick={action.onClick}
                disabled={!hasSelection || action.disabled}
              >
                {action.label}
              </button>
              {tooltip && (
                <span className={styles.tooltipText}>{tooltip}</span>
              )}
            </div>
          );
        })}
        {hasSelection && (
          <button className={styles.clearBtn} onClick={onClear}>
            Clear
          </button>
        )}
      </div>
    </div>
  );
};

BulkActionBar.propTypes = {
  count: PropTypes.number.isRequired,
  itemLabel: PropTypes.string,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      variant: PropTypes.oneOf(['primary', 'secondary', 'danger', 'info']),
      disabled: PropTypes.bool,
      tooltip: PropTypes.string,
      title: PropTypes.string, // legacy alias for tooltip
    })
  ).isRequired,
  onClear: PropTypes.func.isRequired,
  allSelected: PropTypes.bool,
  someSelected: PropTypes.bool,
  onToggleAll: PropTypes.func.isRequired,
};

export default BulkActionBar;
